import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDuration } from '../lib/format.ts';
import type { Facing, Recorded } from '../lib/capture.ts';
import { WAVEFORM_BARS, waveformBars } from '../lib/waveform.ts';
import {
  FlipCameraIcon,
  PauseIcon,
  PlayIcon,
  SendIcon,
  SpeakerIcon,
  TrashIcon,
} from './MediaIcons.tsx';
import './TrimScreen.css';

/**
 * The screen between letting go of a locked recording and sending it: choose an
 * in-point and an out-point, listen back, then send or throw it away.
 *
 * The cut is not made here, and it is not made anywhere. A browser cannot trim
 * an encoded file without decoding and re-encoding it in real time, so what
 * travels with the message is the range — `trimStartMs` and `trimEndMs` — and
 * every player honours it. The seconds outside the range are still inside the
 * uploaded file; they are simply never played.
 */

/** The handoff's minimum: the two handles cannot close past this. */
const MIN_SPAN_PCT = 6;

export interface TrimRange {
  startMs: number;
  endMs: number;
}

interface Range {
  start: number;
  end: number;
}

export function TrimScreen({
  recorded,
  facing,
  canFlip,
  onFlip,
  onDiscard,
  onSend,
}: {
  recorded: Recorded;
  facing: Facing;
  /** False when the flip is switched off at /admin — see RecorderState.canFlip. */
  canFlip: boolean;
  /** Sets which camera the *next* кружочек opens on — see the button below. */
  onFlip: () => void;
  onDiscard: () => void;
  /** `null` means the whole take, which is what an untouched track means. */
  onSend: (trim: TrimRange | null) => void;
}) {
  const [range, setRange] = useState<Range>({ start: 0, end: 100 });
  const [muted, setMuted] = useState(false);
  const [playing, setPlaying] = useState(false);

  const track = useRef<HTMLDivElement>(null);
  const dragging = useRef<keyof Range | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const player = useCallback(
    (): HTMLMediaElement | null => videoRef.current ?? audioRef.current,
    [],
  );

  /**
   * Shows the frame under a handle while it is being dragged, which is what
   * turns the handles from sliders into an edit: a cut point is chosen by what
   * is on screen at it, not by its distance along a strip. Throttled to a
   * frame — a seek per pointermove asks the decoder for more than it can give
   * and the picture stutters behind the finger — and the latest position wins.
   * The video take only: a voice message has nothing to show for a seek.
   */
  const pendingSeekMs = useRef<number | null>(null);
  const seekFrame = useRef<number | null>(null);
  const showFrameAt = useCallback((ms: number) => {
    pendingSeekMs.current = ms;
    if (seekFrame.current !== null) return;
    seekFrame.current = requestAnimationFrame(() => {
      seekFrame.current = null;
      const element = videoRef.current;
      if (element && pendingSeekMs.current !== null) {
        element.currentTime = pendingSeekMs.current / 1000;
      }
    });
  }, []);
  useEffect(
    () => () => {
      if (seekFrame.current !== null) cancelAnimationFrame(seekFrame.current);
    },
    [],
  );

  const isVideo = recorded.mode === 'video';
  // Revoked on unmount: these hold the whole recording in memory until they are.
  const mediaUrl = useMemo(() => URL.createObjectURL(recorded.blob), [recorded.blob]);
  const posterUrl = useMemo(
    () => (recorded.poster ? URL.createObjectURL(recorded.poster) : null),
    [recorded.poster],
  );
  useEffect(
    () => () => {
      URL.revokeObjectURL(mediaUrl);
      if (posterUrl) URL.revokeObjectURL(posterUrl);
    },
    [mediaUrl, posterUrl],
  );

  const bars = useMemo(() => waveformBars(7, WAVEFORM_BARS), []);
  const startMs = Math.round((recorded.durationMs * range.start) / 100);
  const endMs = Math.round((recorded.durationMs * range.end) / 100);

  const pause = useCallback(() => {
    const element = player();
    if (element && !element.paused) element.pause();
    setPlaying(false);
  }, [player]);

  /** For the window-tracked drag below, which never re-subscribes. */
  const rangeRef = useRef(range);
  rangeRef.current = range;

  const nudge = (side: keyof Range, delta: number) => {
    pause();
    const next =
      side === 'start'
        ? { ...range, start: clamp(range.start + delta, 0, range.end - MIN_SPAN_PCT) }
        : { ...range, end: clamp(range.end + delta, range.start + MIN_SPAN_PCT, 100) };
    setRange(next);
    showFrameAt((recorded.durationMs * next[side]) / 100);
  };

  // Dragging is tracked on the window so a finger that slides off the handle
  // goes on moving it.
  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const side = dragging.current;
      const element = track.current;
      if (!side || !element) return;
      if (event.cancelable) event.preventDefault();

      const rect = element.getBoundingClientRect();
      const pct = ((event.clientX - rect.left) / rect.width) * 100;
      // Moving a handle while the preview runs would play past the new
      // out-point, or start before the new in-point.
      pause();
      const current = rangeRef.current;
      const next =
        side === 'start'
          ? { ...current, start: clamp(pct, 0, current.end - MIN_SPAN_PCT) }
          : { ...current, end: clamp(pct, current.start + MIN_SPAN_PCT, 100) };
      setRange(next);
      showFrameAt((recorded.durationMs * next[side]) / 100);
    };
    const onUp = () => {
      dragging.current = null;
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [pause, showFrameAt, recorded.durationMs]);

  /** Plays back only what will be sent, so the handles can be judged by ear. */
  const togglePreview = () => {
    const element = player();
    if (!element) return;
    if (playing) {
      pause();
      return;
    }
    element.currentTime = startMs / 1000;
    void element
      .play()
      .then(() => setPlaying(true))
      .catch(() => setPlaying(false));
  };

  const onTimeUpdate = () => {
    const element = player();
    if (element && element.currentTime * 1000 >= endMs) pause();
  };

  const untouched = range.start < 0.5 && range.end > 99.5;

  const handle = (side: keyof Range, label: string) => (
    <div
      className="trim-handle"
      data-side={side}
      style={{ left: `${range[side]}%` }}
      role="slider"
      tabIndex={0}
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(range[side])}
      aria-valuetext={formatDuration(side === 'start' ? startMs : endMs)}
      onPointerDown={() => {
        dragging.current = side;
      }}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') nudge(side, -2);
        else if (event.key === 'ArrowRight') nudge(side, 2);
        else return;
        event.preventDefault();
      }}
    />
  );

  return (
    <div className="trim-screen" role="dialog" aria-label="Обрезка записи">
      {isVideo ? (
        <>
          {/* Nothing sits on the picture any more. The circle is the one place
              on this screen where the take itself is visible, and every control
              parked on it — the mute in one corner, the flip in another, the
              play badge over the face — was covering the thing being checked.
              They live in the row below; the picture answers only to a tap. */}
          <div className="trim-preview">
            <video
              ref={videoRef}
              className="trim-video"
              src={mediaUrl}
              poster={posterUrl ?? undefined}
              muted={muted}
              playsInline
              preload="metadata"
              onTimeUpdate={onTimeUpdate}
              onClick={togglePreview}
            />
          </div>
          <div className="trim-controls">
            <button
              type="button"
              className="trim-control"
              onClick={() => setMuted((current) => !current)}
              aria-pressed={muted}
              aria-label={muted ? 'Включить звук' : 'Выключить звук'}
            >
              <SpeakerIcon size={16} muted={muted} />
            </button>
            <button
              type="button"
              className="trim-control trim-control-play"
              onClick={togglePreview}
              aria-label={playing ? 'Пауза' : 'Просмотреть'}
            >
              {playing ? <PauseIcon size={18} /> : <PlayIcon size={18} />}
            </button>
            {/* Flipping cannot change a take that is already recorded — the
                frame in the circle is the one that will be sent. What it does
                is choose the camera the next кружочек opens on, which is the
                only honest thing this button can mean at this point in the
                flow. */}
            {canFlip && (
              <button
                type="button"
                className="trim-control"
                onClick={onFlip}
                title="Камера для следующей записи"
                aria-label={
                  facing === 'user'
                    ? 'Следующая запись — на основную камеру'
                    : 'Следующая запись — на фронтальную камеру'
                }
              >
                <FlipCameraIcon size={18} />
              </button>
            )}
          </div>
        </>
      ) : (
        <button
          type="button"
          className="trim-voice"
          onClick={togglePreview}
          aria-label="Прослушать"
        >
          <audio
            ref={audioRef}
            src={mediaUrl}
            muted={muted}
            preload="metadata"
            onTimeUpdate={onTimeUpdate}
          />
          {bars.map((height, index) => (
            <span key={index} className="trim-voice-bar" style={{ height: `${height}px` }} />
          ))}
          <span className="trim-voice-play">
            {playing ? <PauseIcon size={13} /> : <PlayIcon size={13} />}
          </span>
        </button>
      )}

      <div className="trim-track-wrap">
        {/* The handles are siblings of the strip rather than children of it:
            the strip clips its corners with overflow, and a handle inside it
            arrived at 0% or 100% half-swallowed — five visible pixels of an
            already-small target, at exactly the two positions every drag
            starts from. */}
        <div className="trim-clip">
          <div
            ref={track}
            className={`trim-track${isVideo ? ' trim-track-film' : ''}`}
            // The filmstrip is the recording's own frame tiled across the track.
            // Extracting real thumbnails would mean decoding the file again for
            // a strip-of-the-screen decoration.
            style={isVideo && posterUrl ? { backgroundImage: `url(${posterUrl})` } : undefined}
          >
            <div className="trim-scrim" style={{ left: 0, width: `${range.start}%` }} />
            <div className="trim-scrim" style={{ right: 0, width: `${100 - range.end}%` }} />
          </div>
          {handle('start', 'Начало')}
          {handle('end', 'Конец')}
        </div>
        <div className="trim-duration">{formatDuration(endMs - startMs)}</div>
      </div>

      <div className="trim-actions">
        <button type="button" className="trim-discard" onClick={onDiscard} aria-label="Удалить">
          <TrashIcon size={18} />
        </button>
        <button
          type="button"
          className="trim-send"
          onClick={() => onSend(untouched ? null : { startMs, endMs })}
          aria-label="Отправить"
        >
          <SendIcon size={22} />
        </button>
      </div>
    </div>
  );
}

function clamp(value: number, low: number, high: number): number {
  return Math.max(low, Math.min(high, value));
}
